import { useFetch } from "~vue/api";
import type { UserDetails } from "~vue/appTypes";
import { logError } from "~vue/utils/logUtils";
import { computed, inject, type Ref, ref } from "vue";

// POST grants, DELETE revokes. Granting is idempotent server-side, unlike the generic
// ui-interactions endpoint, so a member cannot accumulate consent rows by re-accepting.
export const MIC_CONSENT_ENDPOINT = "/api/accounts/mic-consent";

type ResumeAfterConsent = () => void | Promise<void>;

// Module scope on purpose: a page can mount several mic surfaces (the profile page renders one per
// question), and consent is per member, not per component. Sharing the state means one dialog at a
// time, one write, and accepting on one surface unlocks the rest immediately.
const consentGrantedInThisSession = ref(false);
const isConsentDialogOpen = ref(false);
let resumeAfterConsent: ResumeAfterConsent | null = null;
let onConsentDeclined: (() => void) | null = null;

/**
 * Per-org one-time microphone consent (ENG-1715).
 *
 * Any surface that captures mic audio guards its start path with `micConsentBlocks(resume)`; the
 * dialog is mounted once at the app root, so a surface only has to remember the guard.
 */
export function useMicConsent() {
    const userDetails = inject<Ref<UserDetails>>("user_details");

    const micConsentRequired = computed(() => !consentGrantedInThisSession.value && Boolean(userDetails?.value?.mic_consent_required));

    /**
     * Whether the caller must stop before capturing audio.
     *
     * When it returns true the dialog is now asking, and `resume` runs only if the member accepts
     * and the consent write lands. `onDeclined` is for surfaces that opened the mic on their own
     * (realtime role play) and have nothing to fall back to.
     */
    const micConsentBlocks = (resume: ResumeAfterConsent, onDeclined?: () => void): boolean => {
        if (!micConsentRequired.value) {
            return false;
        }

        resumeAfterConsent = resume;
        onConsentDeclined = onDeclined ?? null;
        isConsentDialogOpen.value = true;
        return true;
    };

    return { micConsentRequired, micConsentBlocks };
}

/**
 * Drives the single root-mounted `MicConsentDialog`. Not for feature code — surfaces go through
 * `useMicConsent`.
 */
export function useMicConsentDialog() {
    const { execute: recordConsent, error: recordConsentError } = useFetch(MIC_CONSENT_ENDPOINT, { immediate: false }).post().json();

    /**
     * The mic is only unlocked once the server has the row: consent that never landed must not
     * start a recording, or a member could speak for a whole session with nothing on file. A failed
     * write leaves the dialog open so the next thing they see is the same ask.
     */
    const acceptMicConsent = async () => {
        await recordConsent();
        if (recordConsentError.value) {
            logError("Failed to record microphone consent; keeping the mic gated and asking again.");
            return;
        }

        consentGrantedInThisSession.value = true;
        isConsentDialogOpen.value = false;

        const resume = resumeAfterConsent;
        resumeAfterConsent = null;
        onConsentDeclined = null;
        await resume?.();
    };

    const declineMicConsent = () => {
        isConsentDialogOpen.value = false;
        resumeAfterConsent = null;

        const declined = onConsentDeclined;
        onConsentDeclined = null;
        declined?.();
    };

    return { isConsentDialogOpen, acceptMicConsent, declineMicConsent };
}

/** Test seam: the shared state outlives a component, so specs have to clear it between cases. */
export function resetMicConsentState() {
    consentGrantedInThisSession.value = false;
    isConsentDialogOpen.value = false;
    resumeAfterConsent = null;
    onConsentDeclined = null;
}
